import { useEffect, useRef, useState, useCallback } from 'react';
import PlaybackPlayer from '../player/PlaybackPlayer';
import MessageQueue from '../live/wsplayer/MessageQueue';

/**
 * useSlotWebCodecs — drive one slot's dragon recorded playback through the WebCodecs
 * PlaybackPlayer, on the SHARED MCPlayback playhead, with DOUBLE-BUFFERED warming so clip
 * boundaries are seamless. The WebCodecs analog of useSlotVideo.
 *
 * WHY (2026-07-17): dragon clips come off engine_backend as plain MP4s (no cache headers);
 * a native <video> re-fetches + shows a loader at every boundary. The reference players
 * (dragon html/playback.js, Java dview) STREAM the file once + decode — immune to that
 * serving. PlaybackPlayer is the ported streaming player.
 *
 * WARMING (port of Java MCPlayerView startPreload/finishPreload + preloadingSlots): the
 * boundary flash is inherent to creating+streaming+decoding a clip's player AT the boundary,
 * because engine_backend's bytes can't be shared between players via cache. So we keep TWO
 * players on two canvases:
 *   - ACTIVE  — plays the current clip, driven by the shared clock, its canvas visible.
 *   - WARM    — the NEXT clip: created while the current one plays, streams the file and
 *               pre-decodes frame 0 onto its hidden canvas (Java startPreload = connect +
 *               buffer, paused).
 * At the boundary the current clip becomes the warm one's URL → we PROMOTE: flip which canvas
 * is visible + drive the (already-loaded, frame-0-decoded) warm player (Java finishPreload =
 * seek 0 + play). No reload, no decode-from-scratch → no flash. The old active player is then
 * reused to warm the new next clip.
 *
 * The shared clock stays MASTER: the active player free-runs at 1x (rAF ≈ wall-clock, matching
 * the 1x tick); we only SEEK it when the shared offset diverges past a tick (scrub / mid-clip
 * entry), never on normal 1x drift.
 *
 * @param {string|null} clipUrl      current clip MP4 URL (null → gap / uninitialised)
 * @param {string|null} nextClipUrl  the clip to warm (immediate next in this track), or null
 * @param {object} opts  offsetInClip, playing, holding, hasClip, onBuffering, onObjectDetection,
 *                        showObjects, showLpr
 * @returns {{canvasARef, canvasBRef, activeBuf:('A'|'B'), ready:boolean, error:?string}}
 *
 * `holding` = the shared buffer-gate is withholding the clock (a sibling cell still loading after a
 * seek). While it holds, the active player PAINTS its first frame but does NOT play, so every cam
 * releases together when the gate clears — the WebCodecs analog of useSlotVideo's gate (Java MCBufferGate:
 * players ride the shared clock, they don't free-run the moment their own decoder is ready).
 */
const SEEK_THRESHOLD = 1.5; // s — looser than one tick so smooth 1x play never re-seeks

export const useSlotWebCodecs = (clipUrl, nextClipUrl, {
  offsetInClip = 0, playing = false, holding = false, hasClip = false, speed = 1,
  onBuffering, onObjectDetection, onMetadataUpdate, showObjects, showLpr,
  // Cloud profile fallback (pro2 → pro1). fallbackUrl/nextFallbackUrl are the main-clip retry for the
  // active/warm clips; profile/fallbackProfile are the numbers the player reports to the Camera-Info
  // overlay (activeProfile flips to fallbackProfile if the pro2 twin 404s). null for the local path.
  fallbackUrl = null, nextFallbackUrl = null, profile = null, fallbackProfile = null,
} = {}) => {
  const canvasARef = useRef(null);
  const canvasBRef = useRef(null);
  const [activeBuf, setActiveBuf] = useState('A');
  const [ready, setReady] = useState(false); // ACTIVE buffer's readiness (drives spinner + effects)
  const [error, setError] = useState(null);
  // Bumped when the ACTIVE buffer paints or fails. `ready` may already be true then (a promoted warm clip
  // that had not painted yet), and the warm-next step below waits on that settle (86bc8571y).
  const [activeSettles, setActiveSettles] = useState(0);

  // Per-buffer bookkeeping (refs — no re-render churn).
  const players = useRef({ A: null, B: null });
  const urls = useRef({ A: null, B: null });
  const readies = useRef({ A: false, B: false });    // decoder CONFIGURED (onReady) — gates play/seek commands
  const rendered = useRef({ A: false, B: false });    // first frame PAINTED (onFirstFrame) — gates the buffer report
  const failed = useRef({ A: null, B: null });        // player's onError message — settles the warm-next wait
  const metadata = useRef({ A: null, B: null });
  // Per-buffer load generation — bumped on every (re)create/teardown so an async player callback that
  // fires after its buffer was superseded (rapid camera-swap / scrub across clip boundaries) is ignored.
  // The player's own _stopped guard already drops decoded frames post-stop; this adds the CLAUDE.md-mandated
  // generation token at the hook layer so a stale onReady/onFirstFrame/onProgress/onMetadataUpdate from a
  // replaced player can't touch this slot's state (e.g. mark a not-yet-ready new player ready, or paint the
  // prior camera).
  const genRef = useRef({ A: 0, B: 0 });
  const activeBufRef = useRef(activeBuf);
  activeBufRef.current = activeBuf;

  // Latest shared-clock values for the async onReady handoff + fresh-player seeding (deps may move
  // before onReady fires; createPlayer stays stable by reading speed here, not from a closure).
  const latest = useRef({});
  latest.current = { offsetInClip, playing, speed, holding, profile, fallbackProfile };

  const onBufRef = useRef(onBuffering);
  onBufRef.current = onBuffering;
  // Dedupe on transition (mirror of useSlotVideo.reportBuffering) so the per-paint onProgress self-heal
  // below can call report(false) freely without invoking onBuffering on every frame.
  const bufferingRef = useRef(false);
  const report = useCallback((b) => {
    if (b === bufferingRef.current) return; // only fire on a real transition
    bufferingRef.current = b;
    if (onBufRef.current) onBufRef.current(b);
  }, []);
  const onMetadataRef = useRef(onMetadataUpdate);
  onMetadataRef.current = onMetadataUpdate;

  // Object-detection queue — shared by both players (only spun up with a subscriber).
  const mqRef = useRef(null);
  useEffect(() => {
    if (onObjectDetection && !mqRef.current) {
      mqRef.current = new MessageQueue();
      mqRef.current.subscribe('objectEvent', onObjectDetection);
    }
    return () => {
      if (mqRef.current && onObjectDetection) mqRef.current.unsubscribe('objectEvent', onObjectDetection);
    };
  }, [onObjectDetection]);

  const canvasFor = (buf) => (buf === 'A' ? canvasARef : canvasBRef).current;

  // Stable accessors for the CURRENTLY-active buffer (read the live ref at call time, so they keep
  // returning the right element/player across A/B promotion without re-subscribing). SlotCell uses
  // these to reach the on-screen canvas (snapshot / drag frame) + the player (info overlay), since
  // the dragon path has no <video> element — the media lives on the canvas.
  const getActiveCanvas = useCallback(() => (activeBufRef.current === 'A' ? canvasARef : canvasBRef).current, []);
  const getActivePlayer = useCallback(() => players.current[activeBufRef.current], []);

  const teardown = useCallback((buf) => {
    const p = players.current[buf];
    if (p) { try { p.stop(); } catch (e) { /* ignore */ } }
    players.current[buf] = null;
    urls.current[buf] = null;
    readies.current[buf] = false;
    rendered.current[buf] = false;
    failed.current[buf] = null;
    metadata.current[buf] = null;
    genRef.current[buf] += 1; // invalidate any in-flight callbacks from the player just stopped
  }, []);

  // Create a player on `buf` for `url`. onReady decides active-vs-warm by the LIVE active
  // buffer (ref), so a warm player promoted before it finishes loading still starts correctly.
  const createPlayer = useCallback((buf, url, playerFallbackUrl) => {
    const canvas = canvasFor(buf);
    if (!canvas) return;
    teardown(buf);
    const gen = genRef.current[buf]; // this player's generation (teardown just bumped it); stale if it changes
    const isStale = () => genRef.current[buf] !== gen;
    urls.current[buf] = url;
    readies.current[buf] = false;
    rendered.current[buf] = false;
    const p = new PlaybackPlayer(canvas, url, {
      autoplay: false,
      showHud: false,
      messageQueue: mqRef.current,
      showObjects,
      showLpr,
      speed: latest.current.speed,
      // Profile fallback + the reported profile (uniform per slot → read from the latest ref, so a
      // conf/zoom change that flips pro2↔pro1 already re-ran this via the URL change).
      fallbackUrl: playerFallbackUrl || null,
      profile: latest.current.profile,
      fallbackProfile: latest.current.fallbackProfile,
      onReady: () => {
        if (isStale()) return; // a newer player took this buffer — ignore the superseded one's config
        // Decoder CONFIGURED (not yet painted). Kick the player toward its target so a frame decodes;
        // do NOT report ready here — the buffer-gate must keep holding until the picture is actually up
        // (onFirstFrame), else two cams "release" at decoder-config and the clock runs ahead of blank
        // canvases. (This was the multi-cam seek/2-clip desync.)
        readies.current[buf] = true;
        const { offsetInClip: off, playing: play, holding: hold } = latest.current;
        if (buf === activeBufRef.current) {
          setError(null);
          if (play && !hold) {
            if (off > SEEK_THRESHOLD) { try { p.seek(off); } catch (e) { /* ignore */ } }
            try { p.play(); } catch (e) { /* ignore */ }
          } else {
            // Paused entry (incl. rehydrate after refresh) OR the shared buffer-gate is holding (a sibling
            // cell still loading after a seek): show a STILL frame at the entry offset so the cell shows
            // the picture, NOT a spinner — and onFirstFrame fires to release THIS cell's gate. Do NOT play
            // here even when `play` is true+held; the play/pause effect starts it the instant the gate
            // clears, so every cam begins together. requestFirstPaint (not a bare seek) retries as bytes
            // stream in if the keyframe isn't buffered yet.
            try { p.requestFirstPaint(off > 0 ? off : 0); } catch (e) { /* ignore */ }
          }
        } else {
          // WARM clip: pre-decode frame 0 onto the hidden canvas + hold (paused), so promotion is
          // instant (Java finishPreload). seek() renders exactly one frame → onFirstFrame marks it ready.
          try { p.seek(0); } catch (e) { /* ignore */ }
        }
      },
      onFirstFrame: () => {
        if (isStale()) return; // superseded player painting into a reused buffer — do not touch state
        // First painted frame (loadeddata analog). NOW the picture is up: for the active buffer release
        // the buffer-gate; for a warm buffer just mark it rendered so a later promotion is instant.
        rendered.current[buf] = true;
        if (buf === activeBufRef.current) { setReady(true); setError(null); report(false); setActiveSettles((n) => n + 1); }
      },
      onProgress: () => {
        if (isStale()) return;
        // Self-heal (mirror of useSlotVideo's timeupdate): a frame painted while playing on the ACTIVE
        // buffer means the picture is genuinely moving → not buffering. Clears a spinner that latched
        // report(true) with no reconcile to clear it (a transient decoder stall/recovery after first paint).
        if (buf === activeBufRef.current) report(false);
      },
      onError: (e) => {
        if (isStale()) return; // a superseded player's error must not surface on the live buffer
        failed.current[buf] = (e && (e.message || String(e))) || 'playback failed';
        if (buf === activeBufRef.current) { setError(failed.current[buf]); report(false); setActiveSettles((n) => n + 1); }
      },
      onMetadataUpdate: (md) => {
        if (isStale()) return; // stale player → do not publish the prior camera's metadata/overlays
        metadata.current[buf] = md;
        if (buf === activeBufRef.current && onMetadataRef.current) onMetadataRef.current(md);
      },
    });
    players.current[buf] = p;
  }, [teardown, report, showObjects, showLpr]);

  // Reconcile the two buffers to { active: clipUrl, warm: nextClipUrl }, REUSING a buffer whose
  // URL already matches — the promotion: a warm buffer holding the now-current clip just becomes
  // active (no reload → no flash).
  useEffect(() => {
    const other = activeBuf === 'A' ? 'B' : 'A';

		// Capability fallback removes both URLs. Release the failed player now so it
		// cannot keep fetching or report stale callbacks behind the native video.
		if (!clipUrl && !nextClipUrl) {
			teardown('A');
			teardown('B');
			setReady(false);
			setError(null);
			report(false);
			return;
		}

    // 1) ACTIVE — only when the playhead is on a clip in THIS track.
    if (hasClip && clipUrl) {
      if (urls.current[activeBuf] === clipUrl) {
        // Ready to the gate only once the frame is PAINTED (rendered), not merely decoder-configured.
        // A failed player is not buffering either: its error hands the clip to the native fallback.
        if (rendered.current[activeBuf]) { setReady(true); report(false); } else if (!failed.current[activeBuf]) { report(true); }
      } else if (urls.current[other] === clipUrl) {
        // PROMOTION — the warm buffer already holds this clip (streamed + frame-0 painted). Flip active;
        // the effect re-runs with the new activeBuf and fills the warm side below. Ready iff it painted.
        // A warm player that failed in the background surfaces its error now instead of spinning.
        if (rendered.current[other]) { setReady(true); report(false); }
        else if (failed.current[other]) { setError(failed.current[other]); report(false); }
        else { report(true); }
        setActiveBuf(other);
        return;
      } else {
        // Neither has it (first clip / scrub / jump): load fresh on the active buffer (flash here
        // is unavoidable — nothing was warmed for this position).
        setReady(false); report(true);
        createPlayer(activeBuf, clipUrl, fallbackUrl);
      }
    } else {
      // GAP in this track — SlotCell paints the "No video at this time" overlay; no active player
      // is needed. But we STILL warm the upcoming clip below, so a gap track's next clip is ready
      // the instant the playhead reaches it (this is what makes OTHER lines seamless, not just the
      // one currently playing — David 2026-07-17).
      setReady(false); report(false);
    }

    // 2) WARM the next upcoming clip — ALWAYS (even in a gap). Every track pre-streams + frame-0-
    //    decodes its next clip on the NON-active buffer, so when the playhead reaches it we promote
    //    with no reload. Skip if that clip is already loaded on either buffer.
    //    Only once the ACTIVE clip has painted (86bc8571y): warming in the same instant as a jump's
    //    active load put twice the requests on the NVR's uplink (and over Safari's six connections), so
    //    the tiles being waited on painted later. In a gap there is no active clip, so it warms at once.
    //    An active clip that failed is settled too: the next clip still warms for the boundary.
    const activeSettled = !(hasClip && clipUrl) || rendered.current[activeBuf] || !!failed.current[activeBuf];
    //    Until then, drop a warm player left from before a jump (neither clip is still wanted): it would
    //    keep downloading beside the active load it is waiting on.
    if (!activeSettled && urls.current[other] && urls.current[other] !== clipUrl && urls.current[other] !== nextClipUrl) {
      teardown(other);
    }
    if (activeSettled && nextClipUrl && nextClipUrl !== clipUrl
        && urls.current[other] !== nextClipUrl && urls.current[activeBuf] !== nextClipUrl) {
      createPlayer(other, nextClipUrl, nextFallbackUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clipUrl, nextClipUrl, fallbackUrl, nextFallbackUrl, hasClip, activeBuf, ready, activeSettles]);

  // A warm buffer can already hold the promoted clip's first-frame metadata.
  // Publish it when that buffer becomes active instead of waiting for playback.
  useEffect(() => {
    const md = metadata.current[activeBuf];
    if (md && onMetadataRef.current) onMetadataRef.current(md);
  }, [activeBuf]);

  // Drive the ACTIVE player's play/pause from the shared clock — GATED by the buffer-gate: while it
  // holds (a sibling still loading after a seek) the player stays paused on its first painted frame,
  // then this effect re-runs when `holding` clears and all cams play together (in-sync, Java MCBufferGate).
  useEffect(() => {
    const p = players.current[activeBuf];
    if (!p || !readies.current[activeBuf]) return;
    if (playing && !holding && hasClip) { try { p.play(); } catch (e) { /* ignore */ } }
    else { try { p.pause(); } catch (e) { /* ignore */ } }
  }, [playing, holding, hasClip, activeBuf, ready]);

  // Drift correction on the ACTIVE player (scrub / rewind / stop — not normal 1x drift).
  useEffect(() => {
    const p = players.current[activeBuf];
    if (!p || !readies.current[activeBuf]) return;
    if (Math.abs((p.playheadSec || 0) - offsetInClip) > SEEK_THRESHOLD) {
      try { p.seek(offsetInClip); } catch (e) { /* ignore */ }
    }
  }, [offsetInClip, activeBuf, ready, hasClip]);

  // Apply speed changes to BOTH players (active free-runs at the new rate; the paused warm player
  // just stores it so a promotion mid-fast-play starts at the right rate). Port of Java setSpeed
  // reaching every slot's player through the coordinator.
  useEffect(() => {
    ['A', 'B'].forEach((buf) => {
      const p = players.current[buf];
      if (p && typeof p.setSpeed === 'function') p.setSpeed(speed);
    });
  }, [speed]);

  // Teardown both players on unmount.
  useEffect(() => () => { teardown('A'); teardown('B'); }, [teardown]);

  return { canvasARef, canvasBRef, activeBuf, ready, error, getActiveCanvas, getActivePlayer };
};

export default useSlotWebCodecs;
